import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { NAV } from "@/constants/testIds";
import {
  LayoutDashboard,
  Truck,
  ClipboardCheck,
  DoorOpen,
  PackageCheck,
  PackagePlus,
  ShieldCheck,
  ShieldOff,
  Footprints,
  Users,
  LogOut,
  Shield,
  Menu,
  X,
  LayoutPanelTop,
  FileText,
  Search,
  Bell,
  BellRing,
  MessageSquare,
  Globe,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import ConnectionStatus from "./ConnectionStatus";
import { pushManager } from "@/lib/push";

const PANELS_CONFIG = [
  {
    titleKey: "panel_master",
    links: [
      { to: "/", labelKey: "dashboard", icon: LayoutDashboard, testid: NAV.dashboard, roles: ["admin", "supervisor", "guardia"] },
      { to: "/master", labelKey: "master_view", icon: Search, testid: "nav-master", roles: ["admin", "supervisor"] },
    ],
  },
  {
    titleKey: "panel_sriuc",
    links: [
      { to: "/siruc/records", labelKey: "records", icon: Truck, testid: NAV.sirucRecords, roles: ["admin", "supervisor", "guardia"] },
      { to: "/siruc/inspections", labelKey: "inspections", icon: ClipboardCheck, testid: NAV.sirucInspections, roles: ["admin", "supervisor", "guardia"] },
      { to: "/siruc/tickets", labelKey: "shipping_tickets", icon: FileText, testid: "nav-siruc-tickets", roles: ["admin", "supervisor", "guardia"] },
    ],
  },
  {
    titleKey: "panel_bitacoras",
    links: [
      { to: "/bitacora/registro_diario", labelKey: "daily_log", icon: FileText, testid: "nav-bit-diario", roles: ["admin", "supervisor", "guardia"] },
      { to: "/bitacora/visita", labelKey: "visits", icon: DoorOpen, testid: NAV.bitacoraVisitas, roles: ["admin", "supervisor", "guardia"] },
      { to: "/recorridos", labelKey: "tours_news", icon: Footprints, testid: NAV.recorridos, roles: ["admin", "supervisor", "guardia"] },
      { to: "/bitacora/embarque", labelKey: "shipments", icon: PackageCheck, testid: NAV.bitacoraEmbarque, roles: ["admin", "supervisor", "guardia"] },
      { to: "/bitacora/recibo", labelKey: "receipts", icon: PackagePlus, testid: NAV.bitacoraRecibo, roles: ["admin", "supervisor", "guardia"] },
      { to: "/bitacora/sellos_colocados", labelKey: "placed_seals", icon: ShieldCheck, testid: NAV.bitacoraSellosColocados, roles: ["admin", "supervisor", "guardia"] },
      { to: "/bitacora/sellos_retirados", labelKey: "broken_seals", icon: ShieldOff, testid: NAV.bitacoraSellosRotos, roles: ["admin", "supervisor", "guardia"] },
      { to: "/bitacora/control_cortinas", labelKey: "curtain_control", icon: LayoutPanelTop, testid: "nav-bitacora-cortinas", roles: ["admin", "supervisor", "guardia"] },
    ],
  },
  {
    titleKey: "panel_config",
    links: [
      { to: "/users", labelKey: "users", icon: Users, testid: NAV.users, roles: ["admin", "supervisor"] },
    ],
  },
];

const MOBILE_LINKS = [
  { to: "/siruc/records", label: "SIRUC", icon: Truck, testid: NAV.sirucRecords },
  { to: "/bitacora/registro_diario", label: "Bitácoras", icon: DoorOpen, testid: NAV.bitacoraVisitas },
  { to: "/recorridos", label: "Rondas", icon: Footprints, testid: NAV.recorridos },
  { to: "/", label: "Panel", icon: LayoutDashboard, testid: NAV.dashboard },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pushPromptVisible, setPushPromptVisible] = useState(false);

  useEffect(() => {
    if (!pushManager.isSupported()) return;
    // Ya concedido antes (ej. otro dispositivo/sesión): sólo re-asegura la suscripción.
    if (pushManager.permission() === "granted") {
      pushManager.ensureIfGranted();
    } else if (pushManager.permission() === "default") {
      setPushPromptVisible(true);
    }

    // Si el usuario toca una notificación push, navegar a la pantalla correspondiente.
    const onMessage = (event) => {
      if (event.data?.type === "sisp-notification-click" && event.data.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker?.addEventListener?.("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onMessage);
  }, [navigate]);

  const enablePushAlerts = async () => {
    const ok = await pushManager.enable();
    setPushPromptVisible(false);
    return ok;
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const renderNavLinks = (onClick) => (
    <div className="space-y-6">
      {PANELS_CONFIG.map((panel) => {
        const visibleLinks = panel.links.filter((l) => l.roles.includes(user?.role));
        if (visibleLinks.length === 0) return null;
        return (
          <div key={panel.titleKey}>
            <p className="px-3 mb-2 text-[10px] font-bold tracking-widest text-white/40 uppercase">
              {t(panel.titleKey)}
            </p>
            <div className="space-y-0.5">
              {visibleLinks.map((l) => (
                <NavItem key={l.to} to={l.to} label={t(l.labelKey)} icon={l.icon} testid={l.testid} onClick={onClick} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  const { data: unreadNotifications } = useQuery({
    queryKey: ["notifications-unread"],
    queryFn: () => api.get("/notifications").then(r => (r.data || []).filter(n => !n.read).length),
    refetchInterval: 30000,
    initialData: 0
  });

  return (
    <div className="min-h-screen bg-secondary flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 flex-col bg-navy text-white shrink-0">
        <div className="p-6 flex items-center gap-3 border-b border-white/10">
          <div className="w-9 h-9 bg-white text-navy flex items-center justify-center">
            <Shield size={20} />
          </div>
          <div>
            <p className="font-heading font-extrabold text-xl tracking-tight leading-none">SISP</p>
            <p className="text-[11px] text-white/50 mt-0.5">Fase 2 · Integral</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-6 px-3">
          {renderNavLinks()}
        </nav>

        {/* Language Switcher */}
        <div className="p-3 border-t border-white/10 flex items-center gap-2">
          <Globe size={14} className="text-white/40" />
          <div className="flex gap-1">
            {['es', 'en', 'zh'].map((lng) => (
              <button
                key={lng}
                onClick={() => changeLanguage(lng)}
                className={`text-[10px] font-bold px-2 py-1 rounded ${i18n.language === lng ? 'bg-white text-navy' : 'text-white/40 hover:text-white'}`}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="p-3 border-t border-white/10 flex items-center justify-around">
          <Link to="/chat" title="Chat del patio" className="p-2 text-white/60 hover:text-white hover:bg-white/5 rounded-full transition-colors">
            <MessageSquare size={20} />
          </Link>
          <Link to="/notifications" title="Notificaciones" className="p-2 text-white/60 hover:text-white hover:bg-white/5 rounded-full transition-colors relative">
            <Bell size={20} />
            {!!unreadNotifications && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-critical text-white text-[8px] flex items-center justify-center font-bold rounded-full">
                {unreadNotifications}
              </span>
            )}
          </Link>
        </div>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-white/50 capitalize">{user?.role}</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid={NAV.logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut size={18} />
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-navy text-white flex items-center justify-between px-4 h-14 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white text-navy flex items-center justify-center">
            <Shield size={18} />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">SISP</span>
        </div>
        <div className="flex items-center gap-1">
          <Link to="/chat" className="p-2 hover:bg-white/10 rounded-full">
            <MessageSquare size={20} />
          </Link>
          <Link to="/notifications" className="p-2 hover:bg-white/10 rounded-full relative">
            <Bell size={20} />
            {!!unreadNotifications && (
              <span className="absolute top-1 right-1 w-3.5 h-3.5 bg-critical text-white text-[7px] flex items-center justify-center font-bold rounded-full border border-navy">
                {unreadNotifications}
              </span>
            )}
          </Link>
          <button onClick={() => setMobileOpen(true)} className="p-2 hover:bg-white/10" aria-label="Abrir menú">
            <Menu size={22} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="relative w-72 max-w-[85%] bg-navy text-white flex flex-col shadow-2xl">
            <div className="p-4 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white text-navy flex items-center justify-center">
                  <Shield size={18} />
                </div>
                <span className="font-heading font-extrabold text-lg">SISP</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-2 hover:bg-white/10" aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-6 px-3">
              {renderNavLinks(() => setMobileOpen(false))}
            </nav>
            <div className="p-3 border-t border-white/10">
              <div className="px-3 py-2 mb-2">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-white/50 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                data-testid={NAV.logout}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                <LogOut size={18} />
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <ConnectionStatus />
        {pushPromptVisible && (
          <div className="fixed bottom-20 lg:bottom-4 right-4 z-[100] max-w-xs bg-navy text-white shadow-2xl rounded-lg p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2">
            <BellRing size={20} className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold">Activar alertas</p>
              <p className="text-xs text-white/70 mt-1">Recibe avisos de entradas, salidas e inspecciones aunque no tengas la app abierta.</p>
              <div className="flex gap-2 mt-3">
                <button onClick={enablePushAlerts} className="text-xs font-bold bg-white text-navy px-3 py-1.5 rounded hover:bg-white/90">Activar</button>
                <button onClick={() => setPushPromptVisible(false)} className="text-xs text-white/60 hover:text-white px-2">Ahora no</button>
              </div>
            </div>
          </div>
        )}
        <main className="flex-1 overflow-y-auto pt-14 lg:pt-0 pb-20 lg:pb-0">
          <Outlet />
        </main>

        {/* Bottom nav for Mobile (All roles for app experience) */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-border grid grid-cols-4 pb-safe">
          {MOBILE_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-3 text-[9px] font-black uppercase tracking-tighter ${
                  isActive ? "text-navy border-t-[3px] border-navy bg-navy/5" : "text-muted-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <l.icon size={22} className={isActive ? "scale-110 transition-transform" : ""} />
                  <span>{l.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testid, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end ?? to === "/"}
      data-testid={testid}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-200 ${
          isActive
            ? "bg-white/10 text-white font-semibold border-l-2 border-white"
            : "text-white/60 hover:bg-white/5 hover:text-white border-l-2 border-transparent"
        }`
      }
    >
      <Icon size={18} className="shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}
